import React from 'react';

/**
 * TierGate Component - Controls access to features based on user tier
 * 
 * Usage Examples:
 * 
 * // Block entire feature for free users
 * <TierGate requiredTier="professional" feature="Advanced Analytics">
 *   <AdvancedAnalyticsComponent />
 * </TierGate>
 * 
 * // Show upgrade prompt overlay
 * <TierGate requiredTier="professional" feature="6M+ Time Range" mode="overlay">
 *   <TimeRangeSelector />
 * </TierGate>
 * 
 * // Limit functionality (e.g., max 10 results for free)
 * <TierGate requiredTier="professional" feature="Full Results" mode="limit" freeLimit={10}>
 *   <SearchResults results={results} />
 * </TierGate>
 */
const TierGate = ({
    children,
    requiredTier = 'professional',
    feature = 'This feature',
    mode = 'redirect', // 'redirect' (default), 'block', 'overlay', 'limit', 'badge'
    freeLimit = null,
    upgradeUrl = '/find-your-plan',
    className = '',
    customMessage = null
}) => {
    // Get user tier from window (set in base.html)
    const userTier = window.userTier || 'anonymous';
    const isLoggedIn = window.isLoggedIn || false;
    
    // Tier hierarchy
    const tierLevels = {
        'anonymous': 0,
        'free': 1,
        'professional': 2,
        'enterprise': 3
    };
    
    const userLevel = tierLevels[userTier] || 0;
    const requiredLevel = tierLevels[requiredTier] || 2;
    const hasAccess = userLevel >= requiredLevel;
    
    // If user has access, render children normally
    if (hasAccess) {
        return <div className={className}>{children}</div>;
    }
    
    // Handle redirect mode - instant redirect, no UI
    React.useEffect(() => {
        if (mode === 'redirect') {
            const targetUrl = !isLoggedIn 
                ? '/login?redirect=' + encodeURIComponent(window.location.pathname) 
                : upgradeUrl;
            window.location.href = targetUrl;
        }
    }, [mode, isLoggedIn, upgradeUrl]);
    
    // Generate upgrade message
    const getUpgradeMessage = () => {
        if (customMessage) return customMessage;
        
        if (!isLoggedIn) {
            return `Sign in to access ${feature}`;
        }
        
        const tierName = requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1);
        return `${feature} requires ${tierName} tier`;
    };
    
    // Handle different restriction modes
    switch (mode) {
        case 'redirect':
            // Return null - redirect happens immediately via useEffect
            return null;
            
        case 'block':
            return (
                <div className={`tier-gate-block ${className}`}>
                    <div className="tier-restriction-message">
                        <div className="restriction-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M12 15v2m0 0v2m0-2h2m-2 0h-2m9-5a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <h3>{getUpgradeMessage()}</h3>
                        <p>Upgrade to unlock this feature and many more.</p>
                        <a href={upgradeUrl} className="upgrade-button">
                            {!isLoggedIn ? 'Sign In' : 'Upgrade Now'}
                        </a>
                    </div>
                </div>
            );
            
        case 'overlay':
            return (
                <div className={`tier-gate-overlay ${className}`}>
                    <div className="tier-gate-content">
                        {children}
                    </div>
                    <div className="tier-restriction-overlay">
                        <div className="restriction-popup">
                            <div className="restriction-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M12 15v2m0 0v2m0-2h2m-2 0h-2m9-5a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </div>
                            <h4>{getUpgradeMessage()}</h4>
                            <a href={upgradeUrl} className="upgrade-button-small">
                                {!isLoggedIn ? 'Sign In' : 'Upgrade'}
                            </a>
                        </div>
                    </div>
                </div>
            );
            
        case 'limit':
            // Clone children and pass limited data if applicable
            const limitedChildren = React.Children.map(children, child => {
                if (React.isValidElement(child) && freeLimit !== null) {
                    // Try to limit results if child has results prop
                    if (child.props.results && Array.isArray(child.props.results)) {
                        return React.cloneElement(child, {
                            ...child.props,
                            results: child.props.results.slice(0, freeLimit)
                        });
                    }
                }
                return child;
            });
            
            return (
                <div className={`tier-gate-limit ${className}`}>
                    {limitedChildren}
                    <div className="tier-limit-notice">
                        <p>Showing {freeLimit} of many results. {getUpgradeMessage()} to see all results.</p>
                        <a href={upgradeUrl} className="upgrade-link">
                            {!isLoggedIn ? 'Sign In' : 'Upgrade Now'}
                        </a>
                    </div>
                </div>
            );
            
        case 'badge':
            return (
                <div className={`tier-gate-badge ${className}`}>
                    {children}
                    <div className="tier-badge-overlay">
                        <span className="tier-badge">
                            {requiredTier.toUpperCase()} ONLY
                        </span>
                    </div>
                </div>
            );
            
        default:
            return <div className={className}>{children}</div>;
    }
};

export default TierGate;
